import React, { useState, useEffect } from 'react';
import { ChevronDownIcon, ExternalLinkIcon, ArrowRightIcon, CheckCircleIcon, UsersIcon, TrendingUpIcon, BrainIcon, Mail, MapPin, Phone, Instagram, Linkedin } from 'lucide-react';

const ManacademyHomePage = () => {
  const [selectedCompany, setSelectedCompany] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleAccessPlatform = () => {
    if (selectedCompany === 'vipplus') {
      window.location.href = '/vipplus/login';
    }
  };

  const features = [
    {
      icon: BrainIcon,
      title: "Innovation",
      description: "Anticiper et maîtriser les technologies émergentes"
    },
    {
      icon: TrendingUpIcon,
      title: "Stratégie",
      description: "Développer une vision stratégique à long terme"
    },
    {
      icon: UsersIcon,
      title: "RSE",
      description: "Intégrer la responsabilité sociétale dans l'ADN de l'entreprise"
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-hidden relative">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-56 h-56 bg-gradient-to-r from-blue-400 to-green-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
      </div>

      {/* Header */}
      <header className="relative z-50 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex-shrink-0 group">
              <a href="/" className="flex items-center space-x-3">
                <div className="relative">
                  <img 
                    src="/CADEMY NOIR.png" 
                    alt="MANACADEMY" 
                    className="h-14 w-auto transition-all duration-300 group-hover:scale-105 drop-shadow-lg"
                  />
                </div>
              </a>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a 
                href="https://www.mana.fr/manacademy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group relative px-6 py-3 bg-gradient-to-r from-orange-400 to-yellow-500 text-white font-semibold rounded-xl transition-all duration-300 hover:from-orange-500 hover:to-yellow-600 hover:shadow-lg hover:-translate-y-0.5 overflow-hidden"
              >
                <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></span>
                <span className="relative z-10 flex items-center space-x-2">
                  <span>À propos</span>
                  <ExternalLinkIcon className="w-4 h-4 transform group-hover:scale-110 transition-transform duration-300" />
                </span>
              </a>
            </nav>
          </div>
        </div>
      </header>

      <main className="relative z-10 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        {/* Background Effects for entire main */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-orange-500/10 via-transparent to-yellow-500/15"></div>
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-orange-400/20 to-yellow-400/20 rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute top-2/4 left-1/4 w-80 h-80 bg-gradient-to-r from-yellow-400/15 to-orange-400/15 rounded-full filter blur-3xl animate-pulse animation-delay-2000"></div>
          <div className="absolute bottom-1/4 right-1/3 w-72 h-72 bg-gradient-to-r from-orange-400/10 to-yellow-400/10 rounded-full filter blur-3xl animate-pulse animation-delay-4000"></div>
        </div>
        
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32">
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 relative z-10 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="text-center max-w-5xl mx-auto">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/20 mb-8 animate-fade-in">
                <span className="text-sm font-medium text-orange-200">Excellence • Innovation • Performance</span>
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight">
                Se former aux{' '}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-400 bg-clip-text text-transparent animate-gradient">
                    transitions
                  </span>
                  <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 to-yellow-400 rounded-full transform scale-x-0 animate-scale-x"></div>
                </span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed font-light">
                Innovation, stratégie et RSE pour les organisations qui façonnent l'avenir
              </p>
              
              <a
                href="https://www.mana.fr/manacademy"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-4 px-10 py-5 text-lg font-semibold text-white rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:-translate-y-2 bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-500 hover:from-orange-500 hover:via-yellow-500 hover:to-orange-600 relative overflow-hidden"
              >
                <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></span>
                <span className="relative z-10">En savoir plus sur Manacademy</span>
                <ExternalLinkIcon className="w-5 h-5 relative z-10 transform group-hover:translate-x-1 transition-transform duration-300" />
              </a>
            </div>
          </div>
        </section>

        {/* Access Platform Section */}
        <section className="relative py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <div className="mb-12">
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                Accédez à votre espace
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Sélectionnez votre organisation pour accéder à votre plateforme de formation personnalisée
              </p>
            </div>

            <div className="max-w-lg mx-auto">
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-10 shadow-2xl border border-white/10 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-yellow-500/5"></div>
                <div className="relative z-10 space-y-8">
                  <div className="text-left">
                    <label htmlFor="company-select" className="block text-sm font-semibold text-orange-200 mb-4 uppercase tracking-wider">
                      Choisissez votre entreprise
                    </label>
                    <div className="relative group">
                      <select
                        id="company-select"
                        value={selectedCompany}
                        onChange={(e) => setSelectedCompany(e.target.value)}
                        className="block w-full px-6 py-4 pr-12 text-base border border-white/20 rounded-xl bg-white/10 backdrop-blur text-white placeholder-gray-300 focus:outline-none focus:ring-4 focus:ring-orange-500/30 focus:border-orange-300/50 appearance-none transition-all duration-300 font-medium group-hover:border-orange-300/40"
                      >
                        <option value="" className="text-gray-900 bg-white">-- Sélectionner --</option>
                        <option value="vipplus" className="text-gray-900 bg-white">🏢 VIPPLUS</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-orange-300 transition-colors duration-300" />
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleAccessPlatform}
                    disabled={!selectedCompany}
                    className={`group relative w-full py-4 px-8 rounded-xl font-semibold text-white transition-all duration-500 overflow-hidden ${
                      selectedCompany
                        ? 'bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-500 hover:from-orange-500 hover:via-yellow-500 hover:to-orange-600 transform hover:-translate-y-1 hover:shadow-2xl cursor-pointer'
                        : 'bg-gray-600 cursor-not-allowed'
                    }`}
                  >
                    <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></span>
                    <div className="relative z-10 flex items-center justify-center space-x-3">
                      <span>Accéder à la plateforme</span>
                      {selectedCompany && (
                        <ArrowRightIcon className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                      )}
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="relative py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Innovation Pédagogique */}
              <div className="group relative bg-white/5 backdrop-blur-xl rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 border border-white/10 hover:border-orange-300/40 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-yellow-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-orange-400 to-yellow-400 flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                    <BrainIcon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-orange-100">
                    Innovation Pédagogique
                  </h3>
                  <p className="text-gray-300 leading-relaxed group-hover:text-gray-200 mb-4">
                    Méthodes d'apprentissage avant-gardistes alliant théorie et pratique immersive, elearning et ateliers d'intelligence collective.
                  </p>
                  <div className="inline-flex items-center px-3 py-1 bg-orange-500/20 border border-orange-400/30 text-orange-200 text-sm font-medium rounded-full">
                    Multitude de formats
                  </div>
                </div>
              </div>

              {/* Accompagnement Personnalisé */}
              <div className="group relative bg-white/5 backdrop-blur-xl rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 border border-white/10 hover:border-orange-300/40 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-yellow-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-orange-400 to-yellow-400 flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                    <UsersIcon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-orange-100">
                    Accompagnement Personnalisé
                  </h3>
                  <p className="text-gray-300 leading-relaxed group-hover:text-gray-200 mb-4">
                    Chaque formation est adaptée aux spécificités et objectifs.
                  </p>
                  <div className="inline-flex items-center px-3 py-1 bg-orange-500/20 border border-orange-400/30 text-orange-200 text-sm font-medium rounded-full">
                    100% sur-mesure
                  </div>
                </div>
              </div>

              {/* Excellence Certifiée */}
              <div className="group relative bg-white/5 backdrop-blur-xl rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 border border-white/10 hover:border-orange-300/40 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-yellow-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-orange-400 to-yellow-400 flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                    <CheckCircleIcon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-orange-100">
                    Excellence Certifiée
                  </h3>
                  <p className="text-gray-300 leading-relaxed group-hover:text-gray-200 mb-4">
                    Organisme de formation certifié (n°11922589292) avec les plus hauts standards qualité.
                  </p>
                  <div className="inline-flex items-center px-3 py-1 bg-orange-500/20 border border-orange-400/30 text-orange-200 text-sm font-medium rounded-full">
                    Qualité garantie
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="relative py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16">
              {/* Logo Animation */}
              <div className="relative inline-block mb-8">
                <img 
                  src="/CADEMY NOIR.png" 
                  alt="MANACADEMY" 
                  className="h-16 w-auto mx-auto transform hover:scale-110 transition-transform duration-300"
                />
              </div>
              
              {/* Title with Enhanced Typography */}
              <h2 className="text-4xl lg:text-6xl font-bold mb-8 leading-tight">
                <span className="bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent">
                  L'excellence au service
                </span>
                <br />
                <span className="bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-400 bg-clip-text text-transparent">
                  de vos ambitions
                </span>
              </h2>
            </div>

            {/* Content Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Left Card - Description */}
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 hover:bg-white/10 transition-all duration-500 group">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-orange-400 to-yellow-400"></div>
                    <span className="text-orange-300 font-semibold text-sm uppercase tracking-wider">Organisme de formation</span>
                  </div>
                  
                  <p className="text-xl text-gray-300 leading-relaxed group-hover:text-gray-200 transition-colors duration-300">
                    Manacademy est l'organisme de formation de <span className="text-white font-semibold">Mana</span>, 
                    dédié à l'accompagnement des grandes transitions.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                      <span className="text-orange-300 font-semibold">Innovation</span>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-300">Technologies émergentes</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                      <span className="text-yellow-300 font-semibold">Stratégie</span>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-300">Vision à long terme</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                      <span className="text-orange-300 font-semibold">RSE</span>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-300">Responsabilité sociétale</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Card - Mission */}
              <div className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 backdrop-blur-xl rounded-3xl p-8 border border-orange-300/20 hover:border-orange-300/40 transition-all duration-500 group">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-yellow-400 to-orange-400 animate-pulse"></div>
                    <span className="text-yellow-300 font-semibold text-sm uppercase tracking-wider">Notre mission</span>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-orange-100 transition-colors duration-300">
                    Façonner les leaders de demain
                  </h3>
                  
                  <p className="text-gray-300 leading-relaxed group-hover:text-gray-200 transition-colors duration-300">
                    Nous accompagnons les organisations dans leur transformation en développant 
                    les compétences essentielles pour naviguer dans un monde en constante évolution.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative overflow-hidden bg-gradient-to-t from-[#052431] via-[#083147] to-[#0c3d5e] text-white">
        {/* subtle animated waves (reduced height/opacity for compact footer) */}
        <div className="pointer-events-none absolute -top-16 left-0 w-full h-44 opacity-8">
          <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 1200 200">
            <path d="M0,120 C300,200 900,40 1200,120 L1200,200 L0,200 Z" fill="url(#g)" />
            <defs>
              <linearGradient id="g" x1="0" x2="1">
                <stop offset="0%" stopColor="#0c3d5e" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#052431" stopOpacity="0.18" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {/* Brand / Animated logo */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <img src="/manal.png" alt="Mana" className="h-10 w-auto object-contain" />
              </div>

              <p className="text-xs text-white/70 max-w-xs">
                Nous aidons organisations & écoles à innover, former et transformer.
              </p>

              <div className="mt-1 flex items-center gap-2">
                <a aria-label="LinkedIn" href="https://www.linkedin.com/company/manadvise/" target="_blank" rel="noreferrer" className="p-1 rounded-full bg-white/6 hover:bg-white/12 transition">
                  <Linkedin className="w-5 h-5 text-white/90" />
                </a>
                <a aria-label="Instagram" href="https://www.instagram.com/manadvise/" target="_blank" rel="noreferrer" className="p-1 rounded-full bg-white/6 hover:bg-white/12 transition">
                  <Instagram className="w-5 h-5 text-white/90" />
                </a>
              </div>

              {/* EdTech France membership logo */}
              <div className="mt-1">
                <div className="text-[9px] text-white/50 uppercase mb-1">Partenaire</div>
                <a href="https://www.edtechfrance.fr" target="_blank" rel="noreferrer" className="inline-block">
                  <img src="/edtechfrance.png" alt="Membre EdTech France" className="h-5 w-auto object-contain opacity-90" />
                </a>
              </div>
            </div>

            {/* Links */}
            <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
              <div>
                <h4 className="text-white font-semibold mb-2">Nos activités</h4>
                <ul className="space-y-1 text-sm text-white/80">
                  <li>
                    <a href="https://www.mana.fr/manadvise" className="hover:text-yellow-400 transition-colors">Manadvise → Conseil</a>
                  </li>
                  <li>
                    <a href="https://www.mana.fr/manamind" className="hover:text-yellow-400 transition-colors">Manamind → edTech</a>
                  </li>
                  <li>
                    <a href="https://www.mana.fr/manacademy" className="hover:text-yellow-400 transition-colors">Manacademy → Formation</a>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-semibold mb-2">Ressources</h4>
                <ul className="space-y-1 text-sm text-white/80">
                  <li>
                    <a href="https://www.mana.fr/resources/academic" target="_blank" rel="noreferrer" className="hover:text-yellow-400 transition-colors">Fiches & outils</a>
                  </li>
                  <li>
                    <a href="https://www.mana.fr/publications" className="hover:text-yellow-400 transition-colors">Articles & publications</a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact / CTA */}
            <div className="flex flex-col justify-between">
              <div>
                <h4 className="text-white font-semibold mb-2">Contact</h4>
                <div className="flex flex-col gap-2 text-sm text-white/80 mb-2">
                  <a className="flex items-center gap-2 hover:text-white transition" href="mailto:contact@mana.fr">
                    <Mail className="w-4 h-4 text-white/80" />
                    <span>contact@mana.fr</span>
                  </a>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-white/80" />
                    <span>+33 6 14 03 17 28</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-white/80" />
                    <span>Paris, France</span>
                  </div>
                </div>
              </div>

              <div className="mt-2 text-xs text-white/60">
                <div className="mb-0">© {new Date().getFullYear()} Mana. Tous droits réservés</div>
              </div>
            </div>
          </div>
        </div>

        {/* bottom wave gradient */}
        <div className="h-3 w-full bg-gradient-to-t from-[#041e26] to-transparent opacity-36" />
      </footer>

      <style jsx>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes scale-x {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        .animate-scale-x {
          animation: scale-x 1s ease-out 0.5s both;
        }
        .animate-fade-in {
          animation: fadeIn 1s ease-out 1s both;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
        .delay-200 { animation-delay: 200ms; }
        .delay-400 { animation-delay: 400ms; }
      `}</style>
    </div>
  );
};

export default ManacademyHomePage;